/**
 * Schedule Meeting CTA Button Component
 * 
 * This component provides a consistent UI for the "Schedule a meeting" CTA buttons
 * used in partial reports to encourage users to convert to a sales conversation.
 */

interface CTAConfig {
  buttonText?: string;
  schedulingUrl?: string;
  messageText?: string;
}

interface CTAInstance {
  element: HTMLElement;
  containerId: string;
}

// Singleton class to manage all CTA buttons
class ScheduleMeetingCTA {
  private config: CTAConfig;
  private instances: CTAInstance[];
  
  constructor() {
    // Default configuration
    this.config = {
      buttonText: 'Schedule a Meeting',
      schedulingUrl: "https://blustream.io/contact-sales?utm_source=pxai_journey&utm_medium=internal&utm_campaign=13746024-PXAI%20Journey%20Builder",
      messageText: 'Schedule a meeting to see the complete report'
    };
    
    // Track instances for cleanup
    this.instances = [];
  }
  
  /**
   * Set global configuration for all future CTA buttons
   * @param config Configuration options
   */
  setConfig(config: CTAConfig): ScheduleMeetingCTA {
    this.config = {...this.config, ...config};
    return this;
  }
  
  /**
   * Create a CTA button for the diagram overlay
   * @param containerId The ID of the container to add the overlay to
   * @param options Optional configuration that overrides the global config
   * @returns The created overlay element
   */
  createDiagramOverlay(containerId: string, options: CTAConfig = {}): HTMLElement | null {
    // Merge options with default config
    const config = {...this.config, ...options};
    
    // Get the container
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container ${containerId} not found`);
      return null;
    }
    
    // Make sure container has position relative
    container.style.position = 'relative';
    
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'partial-report-overlay';
    
    // Create message
    const message = document.createElement('div');
    message.className = 'overlay-message';
    message.textContent = config.messageText || '';
    overlay.appendChild(message);
    
    // Create button
    const button = document.createElement('button');
    button.className = 'brand-button-compact';
    button.textContent = config.buttonText || '';
    
    // Add click handler - always open the URL directly
    button.onclick = function() {
      window.open(config.schedulingUrl, '_blank');
    };
    
    overlay.appendChild(button);
    
    // Easter egg message is now static and handled by partial-report-manager.ts
    
    // Add to container
    container.appendChild(overlay);
    
    // Track this instance
    this.instances.push({element: overlay, containerId});
    
    return overlay;
  }
  
  /**
   * Create a CTA section for the topics tab
   * @param containerId The ID of the container to add the CTA to
   * @param options Optional configuration that overrides the global config
   * @returns The created CTA element
   */
  createTopicsCTA(containerId: string, options: CTAConfig = {}): HTMLElement | null {
    // Merge options with default config
    const config = {...this.config, ...options};
    
    // Get the container
    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`Container ${containerId} not found`);
      return null;
    }
    
    // Create CTA container
    const ctaContainer = document.createElement('div');
    ctaContainer.className = 'topics-cta-container';
    
    // Create message
    const message = document.createElement('div');
    message.className = 'topics-cta-message';
    message.textContent = 'Want to see all engagement topics? Schedule a meeting with our product experts.';
    ctaContainer.appendChild(message);
    
    // Create button
    const button = document.createElement('button');
    button.className = 'brand-button-compact';
    button.textContent = config.buttonText || '';
    
    // Add click handler - always open the URL directly
    button.onclick = function() {
      window.open(config.schedulingUrl, '_blank');
    };
    
    ctaContainer.appendChild(button);
    
    // Add to container
    container.appendChild(ctaContainer);
    
    // Track this instance
    this.instances.push({element: ctaContainer, containerId});
    
    return ctaContainer;
  }
  
  /**
   * Remove all CTA elements
   */
  removeAll(): void {
    this.instances.forEach(instance => {
      if (instance.element && instance.element.parentNode) {
        instance.element.parentNode.removeChild(instance.element);
      }
    });
    this.instances = [];
  }
}

// Create a global singleton instance
(window as any).scheduleMeetingCTA = new ScheduleMeetingCTA();